import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BALLOONS } from "../../lib/media";
import { useLang } from "../../lib/i18n";
import SeasonRibbon from "./SeasonRibbon";
import ToyTrain from "./ToyTrain";

const ROW_META = [
  { id: "spring-summer", img: BALLOONS.sunflower, alt: "Sunflower balloon", xDir: 1,  palette: "yellow" },
  { id: "autumn-winter", img: BALLOONS.leaf,      alt: "Autumn leaf balloon", xDir: -1, palette: "orange" },
  { id: "graduation",    img: BALLOONS.teddy,     alt: "Graduation teddy balloon", xDir: 1,  palette: "purple" },
  { id: "schools",       img: BALLOONS.books,     alt: "Books balloon", xDir: -1, palette: "blue" },
];

/*
  Three-layer scroll-reactive motion:
  - Text (least movement, stays grounded)
  - Main themed balloon (medium movement + slight rotate + scale lock-in at centre)
  - Camera balloon (strongest movement + opposite drift)

  scrollYProgress goes 0 → 1 as the row crosses the viewport.
  We derive multi-stop transforms so motion starts strong, focuses at centre (0.5), and drifts out.
*/

function Row({ row, index }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  // Vertical translate — text subtle, main medium, camera strong
  const yText = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const yMain = useTransform(scrollYProgress, [0, 1], [260, -260]);
  const yCamera = useTransform(scrollYProgress, [0, 1], [420, -420]);

  // Horizontal drift — opposite direction for camera vs main
  const xMain = useTransform(scrollYProgress, [0, 1], [0, 60 * row.xDir]);
  const xCamera = useTransform(scrollYProgress, [0, 1], [0, -80 * row.xDir]);

  // Micro rotation — 1 to 3 degrees max
  const rotMain = useTransform(scrollYProgress, [0, 0.5, 1], [-3, 0, 3]);
  const rotCamera = useTransform(scrollYProgress, [0, 0.5, 1], [3, 0, -3]);

  // Scale lock-in — peaks as row passes through viewport centre
  const scaleMain = useTransform(
    scrollYProgress,
    [0, 0.35, 0.5, 0.65, 1],
    [0.94, 1.02, 1.04, 1.02, 0.94]
  );
  const scaleCamera = useTransform(
    scrollYProgress,
    [0, 0.4, 0.5, 0.6, 1],
    [0.92, 1.0, 1.03, 1.0, 0.92]
  );

  // Opacity focus — fades slightly at far edges, crisp at centre
  const opacityMain = useTransform(
    scrollYProgress,
    [0, 0.2, 0.5, 0.8, 1],
    [0.55, 0.95, 1, 0.95, 0.55]
  );
  const opacityCamera = useTransform(
    scrollYProgress,
    [0, 0.2, 0.5, 0.8, 1],
    [0.5, 0.9, 1, 0.9, 0.5]
  );

  const isRight = index % 2 === 1;

  return (
    <div
      ref={ref}
      data-testid={`season-${row.id}`}
      style={{ position: "relative" }}
      className="relative"
    >
      {/* Decorative twister colour ribbon — sits behind the text on the same side */}
      <SeasonRibbon palette={row.palette} side={isRight ? "right" : "left"} id={row.id} />

      <div className="relative z-[2] max-w-[1400px] mx-auto px-4 sm:px-10 py-6 sm:py-10 flex flex-col sm:flex-row items-center sm:justify-between gap-4 sm:gap-8">
        <motion.div
          style={{ y: yText, willChange: "transform" }}
          className={`w-full sm:w-auto flex items-center justify-center sm:justify-start gap-3 sm:gap-12 ${
            isRight ? "sm:flex-row-reverse sm:ml-auto sm:text-right" : ""
          }`}
        >
          <span className="font-display font-black text-xs sm:text-sm text-black/60 tracking-widest shrink-0">
            0{index + 1}
          </span>
          <h3
            data-testid={`season-heading-${row.id}`}
            className="font-display font-black uppercase leading-none text-black whitespace-nowrap tracking-tight sm:tracking-tighter text-[7.5vw] sm:text-[7vw] lg:text-[5.5vw]"
          >
            {row.label}
          </h3>
        </motion.div>

        <div
          className={`relative flex items-center justify-center gap-4 sm:gap-6 shrink-0 ${
            isRight ? "sm:order-first" : ""
          }`}
        >
          {/* Main themed balloon */}
          <motion.div
            style={{
              y: yMain,
              x: xMain,
              rotate: rotMain,
              scale: scaleMain,
              opacity: opacityMain,
              willChange: "transform, opacity",
            }}
            className="pointer-events-none w-20 h-20 sm:w-32 sm:h-32 lg:w-44 lg:h-44"
          >
            <img
              src={row.img}
              alt={row.alt}
              className="w-full h-full object-contain balloon"
              draggable={false}
            />
          </motion.div>

          {/* Camera balloon — stronger motion, opposite drift */}
          <motion.div
            style={{
              y: yCamera,
              x: xCamera,
              rotate: rotCamera,
              scale: scaleCamera,
              opacity: opacityCamera,
              willChange: "transform, opacity",
            }}
            className="pointer-events-none w-16 h-16 sm:w-28 sm:h-28 lg:w-36 lg:h-36"
          >
            <img
              src={BALLOONS.camera}
              alt="Camera balloon"
              className="w-full h-full object-contain balloon"
              draggable={false}
            />
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default function Seasons() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const yLabel = useTransform(scrollYProgress, [0, 1], [80, -80]);

  const ROWS = ROW_META.map((r) => ({ ...r, label: t(`seasons.${r.id}`) }));

  return (
    <section
      ref={ref}
      id="seasons"
      data-testid="seasons-wrapper"
      className="relative bg-white py-16 sm:py-20 overflow-hidden"
    >
      <motion.div
        style={{ y: yLabel }}
        className="max-w-[1400px] mx-auto px-6 sm:px-10 pb-6"
      >
        <span dir="auto" className="font-body text-[10px] uppercase tracking-[0.3em] font-semibold text-black/50">
          {t("seasons.overline")}
        </span>
      </motion.div>
      <div className="relative">
        {ROWS.map((row, i) => (
          <Row key={row.id} row={row} index={i} />
        ))}
      </div>
    </section>
  );
}
